import React, { useState, useEffect } from 'react';
import { X, Calendar as CalIcon, Users, Check, ChevronRight, ChevronLeft, Loader2, Smartphone, ShieldCheck, CreditCard } from 'lucide-react';
import { publicApi } from '../lib/api';
import { LoyaltyWidget, CouponInput } from './LoyaltyWidget';
import TikTokConsentModal from './TikTokConsentModal';

const TIME_SLOTS = ['08:00', '10:00', '12:00', '14:00', '16:00'];

const PROVIDERS = [
  { id: 'mtn', name: 'MTN Mobile Money', color: '#FFCC00', textColor: '#000' },
  { id: 'moov', name: 'Moov Money', color: '#0066B3', textColor: '#fff' },
  { id: 'celtis', name: 'Celtiis Cash', color: '#E31E24', textColor: '#fff' }
];

const formatFCFA = (n) => new Intl.NumberFormat('fr-FR').format(n);

const ReservationModal = ({ open, onClose, presetPackage }) => {
  const [step, setStep] = useState(1);
  const [packages, setPackages] = useState([]);
  const [pkg, setPkg] = useState(null);
  const [date, setDate] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [passengers, setPassengers] = useState(2);
  const [customer, setCustomer] = useState({ fullName: '', email: '', phone: '' });
  const [notes, setNotes] = useState('');
  const [reservation, setReservation] = useState(null);
  const [provider, setProvider] = useState('mtn');
  const [payPhone, setPayPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payResult, setPayResult] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [payingFedapay, setPayingFedapay] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, discount }

  useEffect(() => {
    if (open) {
      publicApi.getPackages().then(setPackages).catch(() => setPackages([]));
    }
  }, [open]);

  useEffect(() => {
    if (open && presetPackage) {
      setPkg(presetPackage);
    }
    if (!open) {
      // reset on close
      setTimeout(() => {
        setStep(1); setPkg(null); setDate(''); setTimeSlot(''); setPassengers(2);
        setCustomer({ fullName: '', email: '', phone: '' }); setNotes('');
        setReservation(null); setPayPhone(''); setLoading(false); setError(''); setPayResult(null);
        setAppliedCoupon(null);
      }, 300);
    }
  }, [open, presetPackage]);

  const total = pkg ? pkg.price * passengers : 0;
  const discount = appliedCoupon ? Math.floor((total * appliedCoupon.discount) / 100) : 0;
  const finalTotal = total - discount;
  const VAT_RATE = 0.18;
  const finalHT = Math.round(finalTotal / (1 + VAT_RATE));
  const finalVAT = finalTotal - finalHT;

  const startFedaPay = () => setConsentOpen(true);
  const handleConsent = async (accepted) => {
    setConsentOpen(false);
    setPayingFedapay(true); setError('');
    try {
      const res = await publicApi.initFedaPay('reservation', reservation.reference, accepted);
      // Redirect to hosted checkout
      window.location.href = res.checkout_url;
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur paiement FedaPay');
      setPayingFedapay(false);
    }
  };

  const canNext1 = pkg && date && timeSlot && passengers > 0;
  const canNext2 = customer.fullName && /\S+@\S+\.\S+/.test(customer.email) && customer.phone.length >= 8;

  const submitReservation = async () => {
    setLoading(true); setError('');
    try {
      const res = await publicApi.createReservation({
        package_id: pkg.id,
        customer,
        date,
        time_slot: timeSlot,
        passengers,
        total_amount: total,
        notes
      });
      setReservation(res);
      setPayPhone(customer.phone);
      setStep(3);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  const submitPayment = async () => {
    if (!/^\+?\d{8,15}$/.test(payPhone.replace(/\s/g, ''))) {
      setError('Numéro de téléphone invalide'); return;
    }
    setLoading(true); setError('');
    try {
      const res = await publicApi.payMobileMoney({
        reservation_id: reservation.id, provider, phone: payPhone,
        coupon_code: appliedCoupon?.code
      });
      setPayResult(res);
      if (res.success) {
        setStep(4);
      } else {
        setError(res.message);
      }
    } catch (e) {
      setError('Erreur paiement');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 z-[80] flex items-end md:items-center justify-center animate-in fade-in duration-300">
      <div className="absolute inset-0 bg-slate-900/70 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white w-full md:max-w-3xl md:rounded-3xl rounded-t-3xl shadow-2xl max-h-[92vh] overflow-hidden flex flex-col animate-in slide-in-from-bottom-6 duration-500">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#F4A340] to-[#E89534] px-6 py-5 text-white">
          <button onClick={onClose} className="absolute top-4 right-4 w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold">Réserver votre balade</h2>
          <p className="text-white/90 text-sm mt-1">Étape {Math.min(step, 3)} sur 3 · Paiement sécurisé Mobile Money</p>

          {/* Steps indicator */}
          <div className="flex items-center gap-2 mt-4">
            {[1, 2, 3].map((s) => (
              <div key={s} className={`h-1.5 flex-1 rounded-full transition-all ${step >= s ? 'bg-white' : 'bg-white/30'}`} />
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Step 1: choose */}
          {step === 1 && (
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Formule</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {packages.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setPkg(p)}
                      className={`text-left p-4 rounded-2xl border-2 transition-all ${
                        pkg?.id === p.id ? 'border-[#F4A340] bg-orange-50 shadow-md' : 'border-slate-200 hover:border-[#F4A340]/50'
                      }`}
                    >
                      <div className="font-bold text-slate-900">{p.name}</div>
                      <div className="text-xs text-slate-500 mt-1">{p.duration} · {p.subtitle}</div>
                      <div className="text-[#E89534] font-bold mt-2">{formatFCFA(p.price)} F</div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2"><CalIcon className="w-4 h-4 inline mr-1" /> Date</label>
                  <input
                    type="date"
                    min={today}
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2"><Users className="w-4 h-4 inline mr-1" /> Passagers</label>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setPassengers(Math.max(1, passengers - 1))} className="w-11 h-11 rounded-xl border-2 border-slate-200 hover:border-[#F4A340] font-bold">-</button>
                    <div className="flex-1 text-center text-lg font-bold py-2 rounded-xl border-2 border-slate-200">{passengers}</div>
                    <button onClick={() => setPassengers(Math.min(12, passengers + 1))} className="w-11 h-11 rounded-xl border-2 border-slate-200 hover:border-[#F4A340] font-bold">+</button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Heure de départ</label>
                <div className="grid grid-cols-5 gap-2">
                  {TIME_SLOTS.map((t) => (
                    <button
                      key={t}
                      onClick={() => setTimeSlot(t)}
                      className={`py-2.5 rounded-xl border-2 font-medium transition-all ${
                        timeSlot === t ? 'border-[#F4A340] bg-[#F4A340] text-white' : 'border-slate-200 hover:border-[#F4A340]/50 text-slate-700'
                      }`}
                    >{t}</button>
                  ))}
                </div>
              </div>

              {pkg && (
                <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center justify-between">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-widest">Total estimé</div>
                    <div className="text-2xl font-bold text-slate-900">{formatFCFA(total)} <span className="text-sm font-normal text-slate-500">F CFA</span></div>
                  </div>
                  <div className="text-sm text-slate-600">{passengers} × {formatFCFA(pkg.price)}</div>
                </div>
              )}
            </div>
          )}

          {/* Step 2: customer info */}
          {step === 2 && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Nom complet</label>
                <input
                  type="text" value={customer.fullName}
                  onChange={(e) => setCustomer({ ...customer, fullName: e.target.value })}
                  placeholder="Aminata Diallo"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Email</label>
                <input
                  type="email" value={customer.email}
                  onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                  placeholder="aminata@example.com"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none transition-colors"
                />
              </div>

              <LoyaltyWidget email={customer.email} onCouponPick={(code) => setAppliedCoupon({ code, discount: 0, pending: true })} />
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Téléphone</label>
                <input
                  type="tel" value={customer.phone}
                  onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                  placeholder="+229 90 00 00 00"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Notes (optionnel)</label>
                <textarea
                  value={notes} onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                  placeholder="Occasion spéciale, besoins particuliers..."
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none transition-colors resize-none"
                />
              </div>

              <div className="bg-slate-50 rounded-2xl p-4">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-2">Récapitulatif</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-slate-500">Formule</div><div className="font-semibold text-right">{pkg?.name}</div>
                  <div className="text-slate-500">Date</div><div className="font-semibold text-right">{date} · {timeSlot}</div>
                  <div className="text-slate-500">Passagers</div><div className="font-semibold text-right">{passengers}</div>
                  <div className="text-slate-500 border-t pt-2">Total</div>
                  <div className="font-bold text-right text-[#E89534] border-t pt-2">{formatFCFA(total)} F CFA</div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: payment */}
          {step === 3 && reservation && (
            <div className="space-y-5">
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-2xl">
                <ShieldCheck className="w-6 h-6 text-emerald-600 flex-shrink-0" />
                <div>
                  <div className="font-semibold text-emerald-900">Réservation créée · {reservation.reference}</div>
                  <div className="text-sm text-emerald-800">Complétez le paiement pour confirmer.</div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-3">Choisissez votre opérateur Mobile Money</label>
                <div className="grid grid-cols-3 gap-3">
                  {PROVIDERS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setProvider(p.id)}
                      className={`p-4 rounded-2xl border-2 transition-all text-center ${
                        provider === p.id ? 'border-[#F4A340] shadow-md scale-[1.02]' : 'border-slate-200 hover:border-[#F4A340]/50'
                      }`}
                    >
                      <div
                        className="w-12 h-12 mx-auto mb-2 rounded-xl flex items-center justify-center font-bold text-xs"
                        style={{ backgroundColor: p.color, color: p.textColor }}
                      >{p.id.toUpperCase()}</div>
                      <div className="text-xs font-semibold text-slate-700">{p.name}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2"><Smartphone className="w-4 h-4 inline mr-1" /> Numéro mobile</label>
                <input
                  type="tel" value={payPhone}
                  onChange={(e) => setPayPhone(e.target.value)}
                  placeholder="+229 90 00 00 00"
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#F4A340] outline-none transition-colors text-lg font-medium"
                />
                <p className="text-xs text-slate-500 mt-2">Un code de confirmation sera envoyé sur ce numéro. (Simulation démo · aucune vraie transaction)</p>
              </div>

              <div className="bg-slate-900 text-white rounded-2xl p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-white/70 text-sm">Montant à payer</span>
                  <span className="text-xs px-2 py-0.5 bg-white/10 rounded-full uppercase tracking-wider">Sécurisé</span>
                </div>
                {discount > 0 && (
                  <div className="flex justify-between text-xs text-white/60 mb-1">
                    <span>Sous-total</span><span className="line-through">{formatFCFA(total)} F</span>
                  </div>
                )}
                {discount > 0 && (
                  <div className="flex justify-between text-xs text-emerald-300 mb-1">
                    <span>Réduction fidélité</span><span>-{formatFCFA(discount)} F</span>
                  </div>
                )}
                <div className="border-t border-white/20 pt-2 mt-2 space-y-1 text-sm" data-testid="vat-breakdown">
                  <div className="flex justify-between text-white/70"><span>Montant HT</span><span>{formatFCFA(finalHT)} F</span></div>
                  <div className="flex justify-between text-white/70"><span>TVA (18%)</span><span>{formatFCFA(finalVAT)} F</span></div>
                </div>
                <div className="text-3xl font-bold mt-3">{formatFCFA(finalTotal)} <span className="text-lg font-normal text-white/60">F CFA TTC</span></div>
              </div>

              {/* FedaPay button */}
              <button
                onClick={startFedaPay}
                disabled={payingFedapay}
                data-testid="fedapay-pay-btn"
                className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-[#F4A340] to-[#E89534] hover:from-[#E89534] hover:to-[#D48222] disabled:opacity-60 text-white font-bold rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition"
              >
                {payingFedapay ? <Loader2 className="w-5 h-5 animate-spin" /> : <CreditCard className="w-5 h-5" />}
                Payer {formatFCFA(finalTotal)} F avec FedaPay (MTN, Moov, Celtiis, Carte)
              </button>
              {error && <div className="text-red-600 text-sm text-center" data-testid="pay-error">{error}</div>}

              <div className="text-center text-xs text-slate-400">— ou paiement manuel (simulation démo) —</div>

              <CouponInput
                email={customer.email}
                appliedCode={appliedCoupon?.code}
                discountPercent={appliedCoupon?.discount}
                onApply={(code, pct) => setAppliedCoupon({ code, discount: pct })}
                onRemove={() => setAppliedCoupon(null)}
              />
            </div>
          )}

          {/* Step 4: success */}
          {step === 4 && payResult?.success && (
            <div className="text-center py-6">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-6 animate-in zoom-in duration-500">
                <Check className="w-10 h-10 text-emerald-600" strokeWidth={3} />
              </div>
              <h3 className="text-3xl font-bold text-slate-900 mb-2">Paiement confirmé !</h3>
              <p className="text-slate-600 mb-6">Un email de confirmation vous a été envoyé.</p>

              <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 text-left space-y-2 max-w-md mx-auto">
                <div className="flex justify-between"><span className="text-slate-500">Référence</span><span className="font-bold">{reservation?.reference}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Formule</span><span className="font-semibold">{reservation?.package_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="font-semibold">{reservation?.date} · {reservation?.time_slot}</span></div>
                <div className="flex justify-between border-t pt-2"><span className="text-slate-500">Réf paiement</span><span className="font-mono text-sm">{payResult.reference}</span></div>
              </div>

              <p className="text-sm text-slate-500 mt-6">Rendez-vous 15 min avant le départ au quai principal.</p>
            </div>
          )}

          {error && (
            <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
          )}
        </div>

        {/* Footer buttons */}
        {step < 4 && (
          <div className="border-t border-slate-100 p-4 flex items-center justify-between gap-3 bg-white">
            {step > 1 ? (
              <button onClick={() => setStep(step - 1)} className="flex items-center gap-2 px-5 py-3 rounded-full text-slate-700 hover:bg-slate-100 transition-colors font-medium">
                <ChevronLeft className="w-4 h-4" /> Retour
              </button>
            ) : <div />}

            {step === 1 && (
              <button
                onClick={() => setStep(2)}
                disabled={!canNext1}
                className="flex items-center gap-2 px-6 py-3 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 disabled:cursor-not-allowed text-white font-semibold rounded-full transition-colors"
              >Continuer <ChevronRight className="w-4 h-4" /></button>
            )}
            {step === 2 && (
              <button
                onClick={submitReservation}
                disabled={!canNext2 || loading}
                className="flex items-center gap-2 px-6 py-3 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full transition-colors"
              >{loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Traitement...</> : <>Aller au paiement <ChevronRight className="w-4 h-4" /></>}</button>
            )}
            {step === 3 && (
              <button
                onClick={submitPayment}
                disabled={loading || !payPhone}
                className="flex items-center gap-2 px-6 py-3 bg-[#F4A340] hover:bg-[#E89534] disabled:bg-slate-200 text-white font-semibold rounded-full transition-colors"
              >{loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Traitement...</> : <>Payer {formatFCFA(finalTotal)} F <ChevronRight className="w-4 h-4" /></>}</button>
            )}
          </div>
        )}
        {step === 4 && (
          <div className="border-t border-slate-100 p-4 flex justify-center bg-white">
            <button onClick={onClose} className="px-8 py-3 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-full transition-colors">Fermer</button>
          </div>
        )}
      </div>
      <TikTokConsentModal open={consentOpen} onChoice={handleConsent} onClose={() => setConsentOpen(false)} />
    </div>
  );
};

export default ReservationModal;
